// Gerenciamento simples de consentimento de cookies (LGPD).
// Categorias: necessary (sempre on), analytics, marketing.

export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: string; // ISO timestamp
  version: number;
};

export const CONSENT_KEY = "kentro-cookie-consent";
export const CONSENT_VERSION = 1;
export const CONSENT_EVENT = "kentro:cookie-consent-changed";

export function readConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      ts: String(parsed.ts || ""),
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

export function saveConsent(
  partial: { analytics: boolean; marketing: boolean },
): CookieConsent {
  const value: CookieConsent = {
    necessary: true,
    analytics: !!partial.analytics,
    marketing: !!partial.marketing,
    ts: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch {
    /* noop */
  }
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  } catch {
    /* noop */
  }
  // aplica imediatamente
  applyConsent(value);
  return value;
}

export function clearConsent() {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Inicializa o Consent Mode v2 com defaults negados.
 * Deve ser chamado uma vez no bootstrap, ANTES de qualquer script de
 * analytics/marketing ser carregado, para garantir sinais coerentes
 * entre GA4, GTM, Google Ads e Meta Pixel.
 */
export function initConsentDefaults() {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    __kentroConsentDefaultsSet?: boolean;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  if (w.__kentroConsentDefaultsSet) return;
  w.__kentroConsentDefaultsSet = true;

  w.dataLayer = w.dataLayer || [];
  // gtag stub que empurra no dataLayer (padrão oficial do Google).
  if (!w.gtag) {
    w.gtag = function gtag(...args: unknown[]) {
      (w.dataLayer as unknown[]).push(args);
    };
  }
  w.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
}

/**
 * Carrega o gtag.js do Google Analytics. Idempotente.
 */
export function loadGtag() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __kentroGtagLoaded?: boolean };
  if (w.__kentroGtagLoaded) return;
  w.__kentroGtagLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://www.googletagmanager.com/gtag/js?id=G-KFBEE985G3";
  document.head.appendChild(s);
}

export const GTM_CONTAINER_ID = "GTM-NM9DZCMH";

/**
 * Carrega o Google Tag Manager (container GTM-NM9DZCMH). Idempotente.
 * Injeta o snippet <script> no head e o fallback <noscript><iframe> no body.
 * Só deve ser chamada após consentimento de analytics.
 */
export function loadGtm() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __kentroGtmContainerLoaded?: boolean; dataLayer?: unknown[] };
  if (w.__kentroGtmContainerLoaded) return;
  w.__kentroGtmContainerLoaded = true;

  // Snippet oficial do GTM (head)
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(s);

  // Fallback <noscript><iframe> no body (para user agents sem JS que ainda executem CSS/imagens)
  try {
    const ns = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    ns.appendChild(iframe);
    document.body.insertBefore(ns, document.body.firstChild);
  } catch {
    /* noop */
  }
}

/**
 * Aplica os flags do consentimento em GA4, GTM e Meta Pixel.
 * - analytics=true  -> carrega gtag.js + GTM e libera analytics_storage.
 * - marketing=true  -> libera ad_storage / ad_user_data / ad_personalization
 *                      e habilita rastreamento do Meta Pixel via `fbq('consent','grant')`.
 * Mantém os sinais consistentes entre plataformas em todos os casos.
 */
export function applyConsent(consent: CookieConsent) {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  };

  if (consent.analytics) {
    loadGtag();
    loadGtm();
  }


  // Consent Mode v2 update — sempre chamado, com valores coerentes.
  try {
    w.gtag?.("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
    });
  } catch {
    /* noop */
  }

  // Meta Pixel — grant/revoke coerente com marketing.
  try {
    w.fbq?.("consent", consent.marketing ? "grant" : "revoke");
  } catch {
    /* noop */
  }
}

/** Abre o painel de preferências (dispara evento ouvido pelo banner). */
export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("kentro:open-cookie-preferences"));
  } catch {
    /* noop */
  }
}
